import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Building2, GraduationCap, MapPin, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useScope } from "@/context/ScopeContext";
import { getBackendUrl } from "@/lib/backend";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;
const ALL_VALUE = "__all__";

const ChipSelect = ({ label, displayValue, selectValue, disabled, onValueChange, children, testId, icon, minWidth }) => (
  <Select value={selectValue} onValueChange={onValueChange} disabled={disabled}>
    <SelectTrigger
      data-testid={testId}
      className="dl-filter-chip-trigger"
      style={minWidth ? { minWidth } : undefined}
    >
      {icon}
      <span className="dl-filter-chip-label">
        {label} <b>{displayValue}</b>
      </span>
    </SelectTrigger>
    <SelectContent className="dl-filter-chip-content">{children}</SelectContent>
  </Select>
);

export default function ScopeFilter({ variant = "default" }) {
  const { scope, setScope, clearScope } = useScope();
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [schools, setSchools] = useState([]);

  const selectedDistrict = useMemo(
    () => districts.find((d) => d.district_code === scope.districtCode),
    [districts, scope.districtCode],
  );
  const selectedBlock = useMemo(
    () => blocks.find((b) => b.block_code === scope.blockCode),
    [blocks, scope.blockCode],
  );
  const selectedSchool = useMemo(
    () => schools.find((s) => s.udise_code === scope.udiseCode),
    [schools, scope.udiseCode],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/scope/districts`, {
          headers: { "x-skip-scope": "true" },
        });
        if (!cancelled) setDistricts(res.data || []);
      } catch {
        if (!cancelled) setDistricts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hydrate stored scope codes with names (classrooms_toilets matches on names only).
  useEffect(() => {
    if (!scope.districtCode || scope.districtName || !districts.length) return;
    const district = districts.find((d) => d.district_code === scope.districtCode);
    if (district?.district_name) {
      setScope({ districtName: district.district_name });
    }
  }, [districts, scope.districtCode, scope.districtName, setScope]);

  useEffect(() => {
    if (!scope.blockCode || scope.blockName || !blocks.length) return;
    const block = blocks.find((b) => b.block_code === scope.blockCode);
    if (block?.block_name) {
      setScope({ blockName: block.block_name });
    }
  }, [blocks, scope.blockCode, scope.blockName, setScope]);

  useEffect(() => {
    if (!scope.udiseCode || scope.schoolName || !schools.length) return;
    const school = schools.find((s) => s.udise_code === scope.udiseCode);
    if (school?.school_name) {
      setScope({ schoolName: school.school_name });
    }
  }, [schools, scope.udiseCode, scope.schoolName, setScope]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!scope.districtCode) {
        setBlocks([]);
        return;
      }
      try {
        const res = await axios.get(`${API}/scope/districts/${scope.districtCode}/blocks`, {
          headers: { "x-skip-scope": "true" },
        });
        if (!cancelled) setBlocks(res.data || []);
      } catch {
        if (!cancelled) setBlocks([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scope.districtCode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!scope.blockCode) {
        setSchools([]);
        return;
      }
      try {
        const res = await axios.get(`${API}/scope/blocks/${scope.blockCode}/schools?limit=500`, {
          headers: { "x-skip-scope": "true" },
        });
        if (!cancelled) setSchools(res.data || []);
      } catch {
        if (!cancelled) setSchools([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scope.blockCode]);

  const onDistrictChange = (districtCode) => {
    if (!districtCode || districtCode === ALL_VALUE) {
      clearScope();
      return;
    }
    const d = districts.find((x) => x.district_code === districtCode);
    setScope({
      districtCode,
      districtName: d?.district_name || "",
      blockCode: "",
      blockName: "",
      udiseCode: "",
      schoolName: "",
    });
  };

  const onBlockChange = (blockCode) => {
    if (!blockCode || blockCode === ALL_VALUE) {
      setScope({
        blockCode: "",
        blockName: "",
        udiseCode: "",
        schoolName: "",
      });
      return;
    }
    const b = blocks.find((x) => x.block_code === blockCode);
    setScope({
      blockCode,
      blockName: b?.block_name || "",
      udiseCode: "",
      schoolName: "",
    });
  };

  const onSchoolChange = (udiseCode) => {
    if (!udiseCode || udiseCode === ALL_VALUE) {
      setScope({ udiseCode: "", schoolName: "" });
      return;
    }
    const s = schools.find((x) => x.udise_code === udiseCode);
    setScope({ udiseCode, schoolName: s?.school_name || "" });
  };

  const districtLabel = selectedDistrict?.district_name || "District";
  const blockLabel = selectedBlock?.block_name || "All";
  const schoolLabel = selectedSchool?.school_name
    ? selectedSchool.school_name.length > 18
      ? `${selectedSchool.school_name.slice(0, 18)}…`
      : selectedSchool.school_name
    : "All";

  const hasActiveScope = Boolean(scope.districtCode || scope.blockCode || scope.udiseCode);

  if (variant === "chip") {
    return (
      <div className="dl-scope-filters">
        <ChipSelect
          label="Scope"
          displayValue={districtLabel}
          selectValue={scope.districtCode || ALL_VALUE}
          onValueChange={onDistrictChange}
          testId="scope-district"
          minWidth="148px"
          icon={
            <svg className="dl-filter-chip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          }
        >
          <SelectItem value={ALL_VALUE}>District</SelectItem>
          {districts.map((d) => (
            <SelectItem key={d.district_code} value={d.district_code}>
              {d.district_name}
            </SelectItem>
          ))}
        </ChipSelect>

        <ChipSelect
          label="Block"
          displayValue={blockLabel}
          selectValue={scope.blockCode || ALL_VALUE}
          onValueChange={onBlockChange}
          disabled={!scope.districtCode}
          testId="scope-block"
          minWidth="128px"
        >
          <SelectItem value={ALL_VALUE}>All</SelectItem>
          {blocks.map((b) => (
            <SelectItem key={b.block_code} value={b.block_code}>
              {b.block_name}
            </SelectItem>
          ))}
        </ChipSelect>

        <ChipSelect
          label="School"
          displayValue={schoolLabel}
          selectValue={scope.udiseCode || ALL_VALUE}
          onValueChange={onSchoolChange}
          disabled={!scope.blockCode}
          testId="scope-school"
          minWidth="138px"
        >
          <SelectItem value={ALL_VALUE}>All</SelectItem>
          {schools.map((s) => (
            <SelectItem key={s.udise_code} value={s.udise_code}>
              {s.school_name}
            </SelectItem>
          ))}
        </ChipSelect>

        {hasActiveScope && (
          <button
            type="button"
            className="dl-scope-clear"
            onClick={clearScope}
            title="Clear filters"
            aria-label="Clear filters"
            data-testid="scope-clear"
          >
            <X className="dl-scope-clear-icon" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <MapPin className="w-4 h-4" />
        <span className="hidden lg:inline">Scope</span>
      </div>

      <Select value={scope.districtCode || ALL_VALUE} onValueChange={onDistrictChange}>
        <SelectTrigger className="w-[220px] bg-white" data-testid="scope-district">
          <SelectValue placeholder="District (All)" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>All Districts</SelectItem>
          {districts.map((d) => (
            <SelectItem key={d.district_code} value={d.district_code}>
              <span className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {d.district_name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={scope.blockCode || ALL_VALUE} onValueChange={onBlockChange} disabled={!scope.districtCode}>
        <SelectTrigger className="w-[220px] bg-white" data-testid="scope-block">
          <SelectValue placeholder="Block (All)" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>All Blocks</SelectItem>
          {blocks.map((b) => (
            <SelectItem key={b.block_code} value={b.block_code}>
              <span className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {b.block_name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={scope.udiseCode || ALL_VALUE} onValueChange={onSchoolChange} disabled={!scope.blockCode}>
        <SelectTrigger className="w-[260px] bg-white" data-testid="scope-school">
          <SelectValue placeholder="School (All)" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>All Schools</SelectItem>
          {schools.map((s) => (
            <SelectItem key={s.udise_code} value={s.udise_code}>
              <span className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4" />
                <span className="line-clamp-1">{s.school_name}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {hasActiveScope && (
        <Button
          variant="ghost"
          size="sm"
          className="text-slate-600"
          onClick={clearScope}
          title="Clear filters"
          data-testid="scope-clear"
        >
          <X className="w-4 h-4 mr-1" />
          Clear
        </Button>
      )}

      <div className="hidden xl:block text-xs text-slate-500">
        {selectedDistrict?.district_name && <span>District: {selectedDistrict.district_name}</span>}
        {selectedBlock?.block_name && <span> • Block: {selectedBlock.block_name}</span>}
        {selectedSchool?.school_name && <span> • School: {selectedSchool.school_name}</span>}
      </div>
    </div>
  );
}


